import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PrioSpinner, Select } from '@prio365/prio365-react-library';
import { notification } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getUserMe } from '../../../../../apps/main/rootReducer';
import useContactsContext from '../../../../contacts/hooks/useContactsProvider';
import Flex from '../../../../../components/Flex';
import { ProjectId } from '../../../../../models/Types';
import { VisaCardDto } from '../../../../../models/VisaCard';
import { makePrioStyles } from '../../../../../theme/utils';
import { sanitizeFileName } from '../../../../../util/validateFileName';
import {
  apiGetMyVisaCards,
  apiGetVisaCardsByOffice,
  apiUploadReceipt,
} from '../../../api/index';
import FileDropZone from '../FileDropZone';

const useStyles = makePrioStyles((theme) => ({
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing.small,
    borderBottom: `1px solid ${theme.colors.application.border}`,
    marginBottom: theme.spacing.regular,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.small,
    width: '100%',
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.default,
  },
  cardSelect: {
    width: '100%',
  },
}));

interface VisaCardReceiptSectionProps {
  projectId: ProjectId;
}

const VisaCardReceiptSection: React.FC<VisaCardReceiptSectionProps> = ({
  projectId,
}) => {
  //#region ------------------------------ Defaults
  const { t } = useTranslation();
  const classes = useStyles();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const userMe = useSelector(getUserMe);
  const officeId = userMe?.myOfficeId;
  const userMeOfficeRoles = userMe?.prioData.officeRoles ?? {};
  const { getContactById } = useContactsContext();

  const hasOfficeRole: boolean =
    !!userMeOfficeRoles[officeId]?.find(
      (role) =>
        role === 'officeControllerAssistance' || role === 'officeAssistance'
    ) || false;

  const [visaCards, setVisaCards] = useState<VisaCardDto[]>([]);
  const [selectedVisaCardId, setSelectedVisaCardId] = useState<string | null>(
    null
  );
  const [isLoadingCards, setIsLoadingCards] = useState(true);
  const [isUploading, setIsUploading] = useState(false);

  const activeVisaCards = useMemo(
    () => visaCards.filter((card) => card.creditCardState === 'active'),
    [visaCards]
  );

  const visaCardOptions = useMemo(
    () =>
      activeVisaCards.map((card) => {
        const network = card.creditCardNetwork.toUpperCase();
        const number = `•••• ${card.visaCardNumberEnd}`;
        const contact = getContactById(card.ownerUserAzureId);
        const ownerName = contact
          ? `${contact.firstName} ${contact.lastName}`
          : undefined;

        return {
          value: card.visaCardId,
          label: ownerName
            ? `${network} ${number} — ${ownerName}`
            : `${network} ${number}`,
        };
      }),
    [activeVisaCards, getContactById]
  );

  const selectedCard = useMemo(
    () => activeVisaCards.find((c) => c.visaCardId === selectedVisaCardId),
    [activeVisaCards, selectedVisaCardId]
  );
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const loadVisaCards = async () => {
    if (!userMe) return;

    try {
      setIsLoadingCards(true);

      const result =
        hasOfficeRole && officeId
          ? await apiGetVisaCardsByOffice(officeId)
          : await apiGetMyVisaCards();

      if (result.result.ok && result.data) {
        setVisaCards(result.data);
        const activeCards = result.data.filter(
          (card) => card.creditCardState === 'active'
        );
        if (activeCards.length > 0) {
          setSelectedVisaCardId(activeCards[0].visaCardId);
        }
      }
    } catch (error) {
      notification.error({
        message: t('accounting:creditCard.load.loadFailed'),
        description: t('accounting:creditCard.load.loadFailedDescription'),
      });
    } finally {
      setIsLoadingCards(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!selectedVisaCardId) {
      notification.warning({
        message: t('accounting:creditCard.upload.noCardSelected'),
        description: t(
          'accounting:creditCard.upload.noCardSelectedDescription'
        ),
      });
      return;
    }

    const cleanedFile = file.name
      ? new File([file], sanitizeFileName(file.name), { type: file.type })
      : file;

    try {
      setIsUploading(true);

      const result = await apiUploadReceipt(
        cleanedFile,
        selectedVisaCardId,
        'german'
      );

      if (result.result.ok && result.data) {
        notification.success({
          message: t('accounting:creditCard.upload.uploadSuccess'),
          description: t(
            'accounting:creditCard.upload.uploadSuccessDescription'
          ),
        });
      } else {
        throw new Error(`Upload failed with status: ${result.result.status}`);
      }
    } catch (error) {
      notification.error({
        message: t('accounting:creditCard.upload.uploadFailed'),
        description: t('accounting:creditCard.upload.uploadFailedDescription'),
      });
    } finally {
      setIsUploading(false);
    }
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (userMe) {
      loadVisaCards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMe?.id, hasOfficeRole, officeId]);
  //#endregion

  if (isLoadingCards) {
    return (
      <Flex.Row justifyContent="center">
        <PrioSpinner size="default" />
      </Flex.Row>
    );
  }

  if (activeVisaCards.length === 0) {
    return null;
  }

  return (
    <div>
      <div className={classes.sectionHeader}>
        <div className={classes.sectionTitle}>
          <FontAwesomeIcon icon={['fal', 'credit-card']} />
          <h4 style={{ marginBottom: 0 }}>
            {t('accounting:creditCard.title')}
          </h4>
        </div>
      </div>

      <Flex.Column childrenGap={8}>
        {activeVisaCards.length > 1 && (
          <Select
            className={classes.cardSelect}
            value={selectedVisaCardId}
            onChange={setSelectedVisaCardId}
            options={visaCardOptions}
          />
        )}

        {selectedCard && (
          <FileDropZone
            onFileUpload={handleFileUpload}
            isUploading={isUploading}
            projectId={projectId}
            acceptedFileTypes={['.pdf', '.jpg', '.jpeg', '.png', '.tiff']}
            size="small"
            title={t('accounting:creditCard.upload.dropZoneTitle')}
            subtitle={t('accounting:creditCard.upload.dropZoneSubtitle')}
          />
        )}
      </Flex.Column>
    </div>
  );
};

export default VisaCardReceiptSection;
